import { useQuery } from '@tanstack/react-query';
import GenomicsService from '@lifeomic/phc-web-toolkit/src/services/GenomicsService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';

const genomicsService = new GenomicsService();

const TEN_MIN_STALE_TIME = 60 * 1000 * 10;

export interface UseSequenceCountsArguments {
  shortVariantSetId?: string;
  copyNumberSetId?: string;
  structuralSetId?: string;
  expressionSetId?: string;
}

export interface UseSequenceCounts {
  isFetchingShortVariantCounts: boolean;
  isFetchingCopyNumberCounts: boolean;
  isFetchingStructuralCounts: boolean;
  isFetchingExpressionCounts: boolean;
  shortVariantsCount?: number;
  expressionCount?: number;
  structuralCount?: number;
  copyNumberCount?: number;
}

export function useSequenceCounts({
  shortVariantSetId,
  copyNumberSetId,
  structuralSetId,
  expressionSetId,
}: UseSequenceCountsArguments): UseSequenceCounts {
  const auth = useServiceAuth();

  const {
    data: shortVariantsCount,
    fetchStatus: shortVariantsCountFetchStatus,
  } = useQuery(
    ['short-variant-counts', shortVariantSetId],
    async () => {
      const { data } = await genomicsService.getShortVariantsCounts(auth)(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        shortVariantSetId,
      );
      return data?.count;
    },
    { staleTime: TEN_MIN_STALE_TIME, enabled: !!shortVariantSetId },
  );

  const { data: copyNumberCount, fetchStatus: copyNumberCountFetchStatus } =
    useQuery(
      ['copy-number-counts', copyNumberSetId],
      async () => {
        const { data } = await genomicsService.getCopyNumbersCounts(auth)(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          copyNumberSetId,
        );
        return data?.count;
      },
      { staleTime: TEN_MIN_STALE_TIME, enabled: !!copyNumberSetId },
    );

  const { data: structuralCount, fetchStatus: structuralCountFetchStatus } =
    useQuery(
      ['structural-counts', structuralSetId],
      async () => {
        const { data } = await genomicsService.getStructuralVariantsCounts(
          auth,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        )(structuralSetId);
        return data?.count;
      },
      { staleTime: TEN_MIN_STALE_TIME, enabled: !!structuralSetId },
    );

  const { data: expressionCount, fetchStatus: expressionCountFetchStatus } =
    useQuery(
      ['expression-counts', expressionSetId],
      async () => {
        const { data } = await genomicsService.getExpressionsCounts(auth)(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          expressionSetId,
        );
        return data?.count;
      },
      { staleTime: TEN_MIN_STALE_TIME, enabled: !!expressionSetId },
    );

  return {
    isFetchingShortVariantCounts: shortVariantsCountFetchStatus === 'fetching',
    isFetchingCopyNumberCounts: copyNumberCountFetchStatus === 'fetching',
    isFetchingStructuralCounts: structuralCountFetchStatus === 'fetching',
    isFetchingExpressionCounts: expressionCountFetchStatus === 'fetching',
    shortVariantsCount,
    expressionCount,
    structuralCount,
    copyNumberCount,
  };
}
